import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";

// Tiny media-query hook — keeps a single Recharts instance mounted at any time.
// Mounting BOTH the desktop and mobile variants simultaneously triggers a
// width(-1) warning from the hidden ResponsiveContainer on first paint.
function useIsDesktop(breakpoint = 768) {
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : true
  );
  React.useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [breakpoint]);
  return isDesktop;
}

// Date ranges — mirror Phase 1 exec dashboard so the UX stays consistent.
const RANGES = [
  { v: "today", l: "Today" },
  { v: "7d",    l: "7 Days" },
  { v: "30d",   l: "30 Days" },
  { v: "90d",   l: "90 Days" },
  { v: "all",   l: "All Time" },
];

// Gradient: rank #1 is the AMG accent, fades to lighter as rank decreases.
const BAR_COLORS = [
  "#FF1E1E", "#FF3D2E", "#FF5C3D", "#FF7B4D", "#FF8C42", "#F5A623",
  "#EAB308", "#84CC16", "#22C55E", "#14B8A6", "#6FA8DC", "#8E44AD",
  "#F472B6", "#FB7185", "#94A3B8", "#64748B", "#475569", "#334155",
  "#1E293B", "#0F172A",
];

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function FreqTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const r = payload[0].payload;
  return (
    <div className="bg-[#0E0E0E] border border-[#2A2A2A] px-3 py-2 text-xs font-mono-data">
      <div className="text-white font-display normal-case mb-1">#{r.rank} {r.label}</div>
      <div className="text-white/70">Leads: <span className="text-white">{r.count}</span></div>
      <div className="text-white/70">Share: <span className="text-white">{fmtPct(r.pct)}</span></div>
    </div>
  );
}

export default function MakeModelFrequency() {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mobile: Top 10 default; "View More" expands to Top 20 (chart cap).
  const [expanded, setExpanded] = useState(false);
  // Full table toggle — shows the entire ranking beyond Top 20.
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics/make-model-frequency?range=${range}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load make/model frequency"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [range]);

  const allRows = data?.rows || [];
  const top20 = useMemo(() => allRows.slice(0, 20), [allRows]);
  const isDesktop = useIsDesktop();
  // Mobile cap: 10 by default, all top-20 when expanded.
  const mobileChartRows = expanded ? top20 : top20.slice(0, 10);
  const chartRows = isDesktop ? top20 : mobileChartRows;
  // Chart height scales with row count so bars stay readable on long lists.
  const chartHeight = isDesktop
    ? Math.max(200, chartRows.length * 24)
    : Math.max(180, chartRows.length * 26);
  const moreCountMobile = Math.max(0, top20.length - 10);

  return (
    <section className="mb-8" data-testid="make-model-frequency">
      {/* Header + range tabs */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="qd-overline">Make & Model Frequency</div>
          <div className="text-[11px] text-white/45 mt-1">
            Which exact (make + model) combinations are entering the funnel most.
            {data?.total_leads !== undefined && (
              <span className="text-white/30 ml-2">
                · {data.total_leads} leads · {data.unique_models} unique models
              </span>
            )}
          </div>
        </div>
        <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E] overflow-x-auto max-w-full" role="tablist" data-testid="mmf-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.v}
              data-testid={`mmf-range-${r.v}`}
              onClick={() => setRange(r.v)}
              className={`px-2.5 sm:px-3 py-2 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors whitespace-nowrap ${
                range === r.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading make/model frequency…
        </div>
      ) : allRows.length === 0 ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          No leads in this range yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
          {/* LEFT — Horizontal bar chart */}
          <div className="qd-card p-4 sm:p-5" data-testid="mmf-chart-card">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="qd-overline">Top 20 Models</div>
                <div className="text-[11px] text-white/45 mt-1">Horizontal bars · sorted high → low</div>
              </div>
              <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/40">
                Showing {chartRows.length} / {allRows.length}
              </div>
            </div>

            {/* Single Recharts instance driven by useIsDesktop — avoids dual-mount warnings. */}
            <div className="w-full min-w-0" style={{ height: `${chartHeight}px` }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart
                  data={chartRows}
                  layout="vertical"
                  margin={{ top: 4, right: 28, left: isDesktop ? 8 : 4, bottom: 4 }}
                >
                  <XAxis
                    type="number"
                    tick={{ fill: "#666", fontSize: isDesktop ? 10 : 9 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fill: "#bbb", fontSize: isDesktop ? 10 : 9 }}
                    axisLine={false}
                    tickLine={false}
                    width={isDesktop ? 140 : 110}
                    interval={0}
                  />
                  <Tooltip cursor={{ fill: "#FF1E1E10" }} content={<FreqTooltip />} />
                  <Bar dataKey="count" radius={[0, 2, 2, 0]} barSize={isDesktop ? 14 : 12}>
                    {chartRows.map((r, i) => (
                      <Cell key={r.label} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="right"
                      style={{ fill: "#fff", fontSize: isDesktop ? 10 : 9, fontFamily: "monospace" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {moreCountMobile > 0 && !isDesktop && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                data-testid="mmf-mobile-toggle"
                className="mt-3 w-full px-5 py-2 text-[10px] font-mono-data tracking-widest uppercase text-[#FF1E1E] border border-[#1A1A1A] hover:bg-[#FF1E1E]/5 transition-colors"
              >
                {expanded ? "Show Top 10" : `View ${moreCountMobile} more`}
              </button>
            )}
          </div>

          {/* RIGHT — Ranking table */}
          <div className="qd-card p-0 overflow-hidden" data-testid="mmf-table-card">
            <div className="px-4 sm:px-5 py-4 border-b border-[#1A1A1A] flex items-baseline justify-between">
              <div>
                <div className="qd-overline">Ranking Table</div>
                <div className="text-[11px] text-white/45 mt-1">
                  {showFull ? `Full ranking · ${allRows.length} models` : `Top 20 · ${Math.min(20, allRows.length)} models`}
                </div>
              </div>
              {allRows.length > 20 && (
                <button
                  type="button"
                  onClick={() => setShowFull((s) => !s)}
                  data-testid="mmf-fulltable-toggle"
                  className="text-[10px] font-mono-data tracking-widest uppercase text-[#FF1E1E] hover:underline"
                >
                  {showFull ? "Top 20" : "Full ranking"}
                </button>
              )}
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
              <table className="w-full text-xs font-mono-data" data-testid="mmf-table">
                <thead className="sticky top-0 bg-[#0E0E0E]">
                  <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Make</th>
                    <th className="px-3 py-2 text-left">Model</th>
                    <th className="px-3 py-2 text-right">Leads</th>
                    <th className="px-3 py-2 text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {(showFull ? allRows : allRows.slice(0, 20)).map((r) => (
                    <tr
                      key={`${r.make}-${r.model}`}
                      className="border-b border-[#141414] hover:bg-white/[0.02]"
                      data-testid={`mmf-row-${r.rank}`}
                    >
                      <td className="px-3 py-2 text-white/45">#{r.rank}</td>
                      <td className="px-3 py-2 text-white">{r.make}</td>
                      <td className="px-3 py-2 text-white font-display normal-case">{r.model}</td>
                      <td className="px-3 py-2 text-right text-white">{r.count}</td>
                      <td className="px-3 py-2 text-right text-white/80">{fmtPct(r.pct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
